import { apiFetch } from "./client";
import { setTokens, clearTokens, getRefreshToken, setAccessToken } from "@/lib/token-storage";
import { AuthApiError } from "@/lib/auth-errors";

function extractDetail(payload: unknown): string | undefined {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const maybeDetail = (payload as Record<string, unknown>).detail;
    if (typeof maybeDetail === "string" && maybeDetail.trim()) return maybeDetail.trim();
  }
  return undefined;
}

export type BackendProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  onboarding_completed: boolean;
  created_at: string | null;
  updated_at: string | null;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  assigned_trainer_id?: string | null;
  status?: string | null;
  legal_document_approval_needed?: boolean;
  debugger_enabled?: boolean;
};

export async function getMe(): Promise<{ profile: BackendProfile | null; roles: string[] } | null> {
  console.log(`[getMe] Fetching user profile`);
  const res = await apiFetch("/api/auth/me/", { method: "GET" });
  console.log(`[getMe] Response status: ${res.status}`);
  console.log(`[getMe] Response headers:`, Object.fromEntries(res.headers.entries()));
  if (res.status === 401) {
    console.warn(`[getMe] Unauthorized (401) - user not authenticated`);
    return null;
  }
  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    console.error(`[getMe] Request failed: ${res.status} - ${errorText}`);
    throw new Error(`me_failed:${res.status}`);
  }
  const data = await res.json();
  console.log(`[getMe] Success - profile:`, data.profile?.email || "null");
  return data as { profile: BackendProfile | null; roles: string[] };
}

export async function login(email: string, password: string): Promise<void> {
  console.log(`[login] Starting login for: ${email}`);
  const res = await apiFetch("/api/auth/login/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  console.log(`[login] Login response status: ${res.status}`);
  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text().catch(() => "");
    }
    const detail = extractDetail(payload);
    console.error(`[login] Login failed: ${res.status}`, payload);
    throw new AuthApiError({
      action: "login",
      status: res.status,
      detail,
      payload,
      message: detail || `login_failed:${res.status}`,
    });
  }
  
  const data = await res.json();
  if (data.access && data.refresh) {
    setTokens(data.access, data.refresh);
    console.log(`[login] Login successful, tokens stored`);
  } else {
    console.error(`[login] Login response missing tokens`);
    throw new Error("login_failed:no_tokens");
  }
}

export async function logout(): Promise<void> {
  // Clear tokens first (client-side logout)
  clearTokens();
  
  // Optionally call backend logout endpoint
  try {
    const res = await apiFetch("/api/auth/logout/", { method: "POST" });
    if (!res.ok) {
      console.warn(`[logout] Backend logout returned ${res.status}, but tokens cleared`);
    }
  } catch (error) {
    console.warn(`[logout] Backend logout failed, but tokens cleared:`, error);
  }
}

export async function signup(email: string, password: string, firstName?: string, lastName?: string): Promise<void> {
  const res = await apiFetch("/api/auth/signup/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, first_name: firstName || "", last_name: lastName || "" }),
  });
  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text().catch(() => "");
    }
    const detail = extractDetail(payload);
    throw new AuthApiError({
      action: "signup",
      status: res.status,
      detail,
      payload,
      message: detail || `signup_failed:${res.status}`,
    });
  }
}

export async function activateInvitedUser(code: string): Promise<void> {
  const res = await apiFetch(`/api/v1/invited-user/activate/${encodeURIComponent(code)}`, {
    method: "GET",
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: "Failed to activate account" }));
    throw new Error(errorData.detail || `activation_failed:${res.status}`);
  }
}

export async function completeInvitedUserSetup(
  code: string,
  password: string,
  firstName: string,
  lastName: string
): Promise<void> {
  const res = await apiFetch(`/api/v1/invited-user/complete-setup/${encodeURIComponent(code)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      password,
      first_name: firstName,
      last_name: lastName,
    }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: "Failed to complete setup" }));
    throw new Error(errorData.detail || `setup_completion_failed:${res.status}`);
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await apiFetch("/api/v1/reset-password/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: "Failed to request password reset" }));
    throw new Error(errorData.detail || `password_reset_request_failed:${res.status}`);
  }
}

export async function verifyResetCode(code: string): Promise<void> {
  const res = await apiFetch(`/api/v1/reset-password/verify/${encodeURIComponent(code)}`, {
    method: "GET",
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: "Invalid or expired reset code" }));
    throw new Error(errorData.detail || `reset_code_verification_failed:${res.status}`);
  }
}

export async function completePasswordReset(code: string, password: string): Promise<void> {
  const res = await apiFetch(`/api/v1/reset-password/complete/${encodeURIComponent(code)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ detail: "Failed to reset password" }));
    throw new Error(errorData.detail || `password_reset_completion_failed:${res.status}`);
  }
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshToken(): Promise<string | null> {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    console.warn("[refreshToken] No refresh token available");
    return null;
  }

  const external = (import.meta as any).env?.BACKEND_EXTERNAL_URL as string | undefined;
  const urlPath = "/api/auth/refresh/";
  const url = external ? external + urlPath : urlPath;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: refreshTokenValue }),
    });

    if (!res.ok) {
      console.error(`[refreshToken] Token refresh failed: ${res.status}`);
      // If refresh fails, clear tokens
      clearTokens();
      return null;
    }

    const data = await res.json();
    if (data.access) {
      setAccessToken(data.access);
      console.log(`[refreshToken] Token refreshed successfully`);
      return data.access;
    }
    return null;
  } catch (error) {
    console.error("[refreshToken] Token refresh error:", error);
    clearTokens();
    return null;
  }
}


